// A row on an outreach thread is not always the prospect writing back. The
// response poll also records delivery-failure notices (NDRs) and our own replies
// composed directly in Outlook rather than through the app, so that a stored
// thread matches what the prospect actually saw.
//
// Rendering MUST distinguish them: a bounce or one of our own messages shown as
// a reply reads as interest, which is the one mistake this data can cause.
//
// Mirrors flattened_kind() in the backend (client/serializers.py).
export const RESPONSE_KIND = {
  // Composed and sent through this app.
  SENT: 'sent',
  REPLY: 'reply',
  // An out-of-office or other machine-sent answer. Shown in full -- it often
  // names who to write to instead -- but it is not the prospect responding.
  AUTO_REPLY: 'auto_reply',
  BOUNCE: 'bounce',
  // Ours, but sent by hand from Outlook rather than through the app.
  OUTBOUND: 'outbound',
}

const SENT_META = {
  label: 'Sent',
  badgeClass: 'bg-gray-100 text-gray-600',
  cardClass: 'bg-white border-gray-200',
  isReply: false,
}

const KIND_META = {
  [RESPONSE_KIND.SENT]: SENT_META,
  [RESPONSE_KIND.REPLY]: {
    label: 'Reply',
    badgeClass: 'bg-emerald-100 text-emerald-700',
    cardClass: 'bg-white border-gray-200',
    isReply: true,
  },
  [RESPONSE_KIND.AUTO_REPLY]: {
    label: 'Auto-reply',
    badgeClass: 'bg-gray-100 text-gray-600',
    cardClass: 'bg-gray-50 border-gray-200',
    isReply: false,
  },
  [RESPONSE_KIND.BOUNCE]: {
    label: 'Bounce',
    badgeClass: 'bg-red-100 text-red-700',
    // Tinted so a failure doesn't read as a normal message at a glance.
    cardClass: 'bg-red-50 border-red-200',
    isReply: false,
  },
  // Rendered exactly like an app send. Which tool one of our emails was sent
  // from doesn't matter to anyone reading the conversation -- only that it was
  // ours. The kind is still recorded (and filterable on the dev feed) for the
  // Outlook-vs-app metric.
  [RESPONSE_KIND.OUTBOUND]: SENT_META,
}

const UNKNOWN_META = {
  label: 'Unknown',
  badgeClass: 'bg-gray-100 text-gray-600',
  cardClass: 'bg-white border-gray-200',
  isReply: false,
}

// A missing kind means the backend hasn't started returning the field yet (the
// column defaults to 'reply', and every row that predates it is a reply), so it
// falls back to Reply rather than rendering as Unknown during a deploy where the
// frontend lands first. An unrecognised non-empty value is shown as-is instead,
// so a kind added server-side is visible rather than silently mislabelled.
export function kindMeta(kind) {
  if (!kind) return KIND_META[RESPONSE_KIND.REPLY]
  return KIND_META[kind] || { ...UNKNOWN_META, label: kind }
}

export function isReply(kind) {
  return kindMeta(kind).isReply
}

// Filter options for the dev feed, in the order they're most often wanted.
export const KIND_FILTER_OPTIONS = [
  { value: RESPONSE_KIND.REPLY, label: 'Replies' },
  { value: RESPONSE_KIND.AUTO_REPLY, label: 'Auto-replies' },
  { value: RESPONSE_KIND.OUTBOUND, label: 'Sent from Outlook' },
  { value: RESPONSE_KIND.BOUNCE, label: 'Bounces' },
]

// "3 sent · 2 replies" -- only the kinds actually present. Outlook sends count as
// sent, for the same reason they render as sent.
export function summarizeKinds(messages) {
  const counts = { sent: 0, reply: 0, auto_reply: 0, bounce: 0, other: 0 }
  ;(messages || []).forEach((m) => {
    const k = m.kind === RESPONSE_KIND.OUTBOUND ? RESPONSE_KIND.SENT : (m.kind || RESPONSE_KIND.REPLY)
    if (counts[k] === undefined) counts.other += 1
    else counts[k] += 1
  })
  const parts = []
  if (counts.sent) parts.push(`${counts.sent} sent`)
  if (counts.reply) parts.push(`${counts.reply} ${counts.reply === 1 ? 'reply' : 'replies'}`)
  if (counts.auto_reply) parts.push(`${counts.auto_reply} auto-${counts.auto_reply === 1 ? 'reply' : 'replies'}`)
  if (counts.bounce) parts.push(`${counts.bounce} ${counts.bounce === 1 ? 'bounce' : 'bounces'}`)
  if (counts.other) parts.push(`${counts.other} other`)
  return parts.join(' · ')
}
